import { Link } from "react-router-dom";
import { BedDouble, Bath, Car, Maximize, MessageCircle, Instagram } from "lucide-react";
import { useSite } from "@/lib/site";
import { money, photoUrl, waLink } from "@/lib/api";

const statusStyle = {
  disponible: "bg-emerald-50 text-emerald-700 border-emerald-200",
  reservada: "bg-amber-50 text-amber-700 border-amber-200",
  alquilada: "bg-[#FF3333] text-white border-[#FF3333]",
};

export default function PropertyCard({ p }) {
  const { settings, t } = useSite();
  const cover = photoUrl(p.photos?.[0]);
  const msg = p.status === "alquilada"
    ? `Hola SoloRentaPC, vi la propiedad ${p.code} - ${p.title} (${p.zone}) que ya está alquilada. ¿Tienen algo similar disponible?`
    : `Hola SoloRentaPC, me interesa la propiedad ${p.code} - ${p.title} (${p.zone}). ¿Sigue disponible?`;
  const img = <img src={cover} alt={p.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />;
  return (
    <article className="flex flex-col h-full rounded-2xl overflow-hidden border border-slate-200/80 bg-white shadow-sm hover:shadow-xl hover:-translate-y-1 transition-[box-shadow,transform] duration-300" data-testid={`property-card-${p.code}`}>
      <div className="relative aspect-[4/5] w-full overflow-hidden group bg-slate-100">
        {p.instagram_url ? (
          <a href={p.instagram_url} target="_blank" rel="noreferrer" data-testid={`property-card-photo-${p.code}`} aria-label="Ver en Instagram">
            {img}
            <span className="absolute bottom-3 right-3 bg-white/90 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"><Instagram size={16} /></span>
          </a>
        ) : (
          <Link to={`/propiedad/${p.code}`} data-testid={`property-card-photo-${p.code}`}>{img}</Link>
        )}
        <span className={`absolute top-3 left-3 text-xs font-semibold px-2.5 py-1 rounded-full border capitalize ${statusStyle[p.status] || statusStyle.disponible}`}>{p.status}</span>
        <span className="absolute top-3 right-3 text-[11px] font-mono-num bg-black/60 text-white px-2 py-1 rounded-md">{p.code}</span>
      </div>
      <div className="flex flex-col flex-1 p-5 gap-3">
        <div className="flex items-baseline justify-between gap-2">
          <p className="font-display text-xl font-extrabold text-slate-900" data-testid={`property-card-price-${p.code}`}>{p.price > 0 ? <>{money(p.price, p.currency)}<span className="text-sm font-medium text-slate-500">{t("perMonth")}</span></> : (p.status === "alquilada" ? "Rentada" : "Consultar")}</p>
          <span className="text-xs text-slate-500 shrink-0">{p.type}</span>
        </div>
        <h3 className="font-semibold text-slate-800 line-clamp-1">{p.title}</h3>
        <p className="text-sm text-slate-500 line-clamp-1">{p.zone}{p.residential ? ` · ${p.residential}` : ""}</p>
        <div className="flex items-center gap-4 text-sm text-slate-600 border-t border-slate-100 pt-3 mt-auto">
          {(p.bedrooms > 0 || p.type === "Estudio") && <span className="flex items-center gap-1"><BedDouble size={15} />{p.bedrooms}</span>}
          {p.bathrooms > 0 && <span className="flex items-center gap-1"><Bath size={15} />{p.bathrooms}</span>}
          {p.parking > 0 && <span className="flex items-center gap-1"><Car size={15} />{p.parking}</span>}
          {p.area_m2 > 0 && <span className="flex items-center gap-1"><Maximize size={15} />{p.area_m2} m²</span>}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Link to={`/propiedad/${p.code}`} className="text-center text-sm font-semibold rounded-full border border-slate-200 py-2.5 hover:border-slate-900 transition-colors" data-testid={`property-card-details-${p.code}`}>{t("details")}</Link>
          <a href={waLink(settings.whatsapp, msg)} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 text-sm font-semibold rounded-full bg-[#25D366] hover:bg-[#1fb457] text-white py-2.5 transition-colors" data-testid={`property-card-whatsapp-${p.code}`}><MessageCircle size={15} />{t("whatsapp")}</a>
        </div>
      </div>
    </article>
  );
}
